import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { retry, throwError, timer } from 'rxjs';

// Railway puts the API to sleep when idle. The first request after a cold start
// fails FAST (status 0 — connection reset while the container boots — or a
// 502/503/504 from the edge), then the next one succeeds once the app is up.
// Without this, that first failure surfaces a "Server error" toast. We retry
// only the statuses where the request provably never reached/was-processed by
// the app, so a non-idempotent call (POST/PUT) can't be double-applied:
//   0   → no connection established (request never sent to the app)
//   502 → edge has no upstream yet (app still booting)
//   503 → app unavailable
//   504 → upstream didn't respond in time during wake-up
// Real application errors (400/401/403/409/5xx with a body) are NOT retried —
// they're deterministic and must surface immediately.
const RETRY_MAX_ATTEMPTS = 3;
// Linear backoff: first retry waits RETRY_BASE_DELAY_MS, then +RETRY_STEP_MS each
// time ⇒ 2s, 3s, 4s (up to ~9s total) — long enough for a Railway wake-up, short
// enough that a genuinely-down API still fails reasonably fast.
const RETRY_BASE_DELAY_MS = 2000;
const RETRY_STEP_MS = 1000;
const RETRYABLE_STATUSES = new Set([0, 502, 503, 504]);

export const retryInterceptor: HttpInterceptorFn = (req, next) =>
  next(req).pipe(
    retry({
      count: RETRY_MAX_ATTEMPTS,
      delay: (error, attempt) => {
        if (
          error instanceof HttpErrorResponse &&
          RETRYABLE_STATUSES.has(error.status)
        ) {
          return timer(RETRY_BASE_DELAY_MS + (attempt - 1) * RETRY_STEP_MS);
        }
        // Non-transient: stop retrying and propagate the original error now.
        return throwError(() => error);
      },
    }),
  );
